<template>
  <div class="uploader">
    <input
      style="display: none"
      :accept="accept"
      ref="file"
      type="file"
      multiple
      @change="filesSelected($event.target.files)"
    />
    <input
      style="display: none"
      :accept="accept"
      ref="immediateFile"
      type="file"
    />
    <slot
      :queue="queue"
      :items="items"
      :uploading="uploading"
      :bytesLoaded="bytesLoaded"
      :bytesTotal="bytesTotal"
      :percent="percent"
      :state="state"
    ></slot>
  </div>
</template>
<script>
const STATUS_UPLOADING = "uploading";
const STATUS_COMPLETE = "complete";
const STATUS_READY = "ready";
const STATUS_ERROR = "error";

const EVENT_UPLOAD_CANCEL = "upload.cancel";
const EVENT_UPLOAD_START = "upload.start";
const EVENT_UPLOAD_COMPLETE = "upload.complete";
const EVENT_UPLOAD_PROGRESS = "upload.progress";
const EVENT_UPLOAD_ERROR = "upload.error";
import { EventDispatcher } from "@qikdev/sdk";

export default {
  props: {
    accept: {
      type: String,
    },
  },
  data() {
    return {
      queue: [],
      state: STATUS_READY,
      defaultScopes: [],
      defaultTags: [],
      defaultFreeformTags: [],
      ui: false,
    };
  },
  mounted() {
    this.$glossary.ensure();
  },
  computed: {
    glossary() {
      return this.$glossary.hash;
    },
    outstanding() {
      return this.queue.filter(function (item) {
        return item.state !== STATUS_COMPLETE;
      });
    },
    uploading() {
      return this.state == STATUS_UPLOADING;
    },
    bytesLoaded() {
      var self = this;
      return self.outstanding.reduce(function (set, item) {
        set += item.bytesLoaded || 0;
        return set;
      }, 0);
    },
    bytesTotal() {
      var self = this;
      return self.outstanding.reduce(function (set, item) {
        set += item.bytesTotal || item.file.size;
        return set;
      }, 0);
    },
    percent() {
      var self = this;
      if (self.state != STATUS_UPLOADING) {
        return 0;
      }

      return Math.floor((self.bytesLoaded * 100) / self.bytesTotal);
    },
  },
  methods: {
    select() {
      this.$refs.file.click();
    },
    async selectAndUpload(options) {
      options = options || {};
      const self = this;
      const input = self.$refs.immediateFile;

      return new Promise(function (resolve, reject) {
        // Add a listener
        input.addEventListener("change", fileSelected);

        function fileSelected({ target }) {
          input.removeEventListener("change", fileSelected);
          const files = target.files || [];
          if (!files || !files.length) {
            return;
          }

          const firstFile = files[0];
          const queueItem = self.addFile(firstFile, {
            silent: true,
            data: options.data || {},
            scopes: options.scopes || [],
            tags: options.tags || [],
          });

          resolve(queueItem);

          // Start Uploading immediately
          self.uploadFile(queueItem);
        }

        // click the file input
        input.click();
      });
    },
    filesSelected(files, immediate) {
      const self = this;

      if (!files || !files.length) {
        return;
      }

      self.defaultScopes = [];
      self.defaultTags = [];
      self.defaultFreeformTags = [];

      [...files].forEach((f) => {
        self.addFile(f);
      });
    },
    createObjectURL(file) {
      return window.URL
        ? window.URL.createObjectURL(file)
        : window.webkitURL.createObjectURL(file);
    },
    show() {
      this.ui = true;
    },
    hide() {
      this.ui = false;
    },
    addFile(file, options) {
      options = options || {};
      var self = this;
      var fileName = file.name;
      var fileSize = file.size;
      var fileMime = file.type;
      var contentType =
        self.$sdk.files.getBinaryTypeFromMime(fileMime) || "file";
      var uniqueKey = `${fileName}-${fileSize}`;

      ////////////////////////

      if (options.silent) {
        // Don't add it to the queue
      } else {
        var alreadyInQueue = self.queue.some(function (item) {
          return uniqueKey == `${item.name}-${item.size}`;
        });

        if (alreadyInQueue) {
          //Avoid uploading the same file multiple times
          return;
        }
      }

      ////////////////////////

      var queueItem = {
        //Binary properties
        name: fileName,
        size: fileSize,
        mime: fileMime,
        webkitRelativePath: file.webkitRelativePath,
        file,
        expanded: false,
        local: self.createObjectURL(file),

        //File upload properties
        state: STATUS_READY,
        bytesLoaded: 0,
        bytesTotal: 0,
        percent: 0,
        result: null,
        rendered: false,

        //Now the content itself
        item: {
          title: fileName,
          meta: {
            type: contentType,
            //definition: contentType,
            scopes: options.scopes || [],
            tags: options.tags || [],
          },
          data: {
            ...(options.data || {}),
          },
        },
      };

      ////////////////////////

      // Add event features to the item
      const dispatcher = new EventDispatcher();
      dispatcher.bootstrap(queueItem);

      ////////////////////////

      //Add it in to the queue
      self.queue.unshift(queueItem);

      return queueItem;
    },
    cancelFile(file) {
      //We're not uploading this file
      //so there's nothing to cancel
      if (file.state != STATUS_UPLOADING) {
        return;
      }

      if (file.cancel) {
        file.cancel("Cancelled");
      }

      //Reset the file properties
      file.bytesLoaded = 0;
      file.percent = 0;
      file.state = STATUS_READY;

      file.dispatch(EVENT_UPLOAD_CANCEL);
    },
    removeFile(file) {
      var index = this.queue.indexOf(file);
      this.cancelFile(file);
      this.queue.splice(index, 1);
    },
    uploadNextFile() {
      var self = this;

      //The uploader has paused or hasn't started
      //So this function should just stop early
      if (self.state != STATUS_UPLOADING) {
        return;
      }

      //Get the next item in the list that is ready for upload
      var nextItem = self.queue.find(function (file) {
        return file.state == STATUS_READY;
      });

      //If there isn't any more to upload
      //then we've finished the whole queue
      if (!nextItem) {
        self.state = STATUS_READY;
        self.$sdk.notify(`Upload Complete!`, { lifetime: 10000 });
        // self.queue.length = 0;
        return;
      }

      /////////////////////////////////

      //Let's start uploading the file
      self.uploadFile(nextItem);
    },
    async uploadFile(file) {
      var self = this;

      //If the file is complete or is already being uploaded
      switch (file.state) {
        case STATUS_COMPLETE:
        case STATUS_UPLOADING:
          //Ignore the request to upload it
          return;
          break;
      }

      /////////////////////

      file.state = STATUS_UPLOADING;
      file.dispatch(EVENT_UPLOAD_START);

      // Run Precheck
      const precheckEnabled = true;
      if (precheckEnabled) {
        const fileSize = file.size;
        const fileName = file.name;
        const fileType = file.mime;

        const { data: existing } = await self.$sdk.api.post(`/file/check`, {
          fileSize,
          fileName,
          fileType,
        });

        if (existing && existing.exists) {
          //Store the result
          file.result = existing;
          file.state = STATUS_COMPLETE;

          var fileMeta = file.result.meta;

          self.$sdk.notify(`${file.result.title} was uploaded successfully`);

          file.dispatch(EVENT_UPLOAD_COMPLETE, existing);
          //Upload the next file in the queue
          self.uploadNextFile();
          return;
        }
      }

      /////////////////////

      var itemData = Object.assign({}, file.item || {});

      if (!itemData.meta) {
        itemData.meta = {};
      }

      //No scopes have been specified
      if (!itemData.meta.scopes || !itemData.meta.scopes.length) {
        itemData.meta.scopes = self.defaultScopes;
      }

      //Extra properties and details
      //for the item

      if (!itemData.tags) {
        itemData.tags = [];
      }

      if (self.defaultFreeformTags.length) {
        itemData.tags = [...itemData.tags, ...self.defaultFreeformTags];
      }

      /////////////////////

      var formData = new FormData();
      formData.set("json", JSON.stringify(itemData));
      formData.append("file", file.file, file.name);

      //Make the request
      var body = formData;
      var config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          let percentCompleted = Math.floor(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          file.percent = percentCompleted;
          file.bytesLoaded = progressEvent.loaded;
          file.bytesTotal = progressEvent.total;

          file.dispatch(EVENT_UPLOAD_PROGRESS, progressEvent);
        },
      };

      /////////////////////

      //Create a cancel token
      const CancelToken = self.$sdk.api.CancelToken;
      const source = CancelToken.source();
      config.cancelToken = source.token;
      file.cancel = source.cancel;

      /////////////////////

      //Make the request
      let uploadURL = `/file/upload`;
      if (self.$sdk.fileAPI) {
        // Upload binaries to the main API
        uploadURL = `${self.$sdk.fileAPI}${uploadURL}`;
      }

      var promise = self.$sdk.api.post(uploadURL, body, config);

      promise.then(function (res) {
        //Store the result
        file.result = res.data;
        file.state = STATUS_COMPLETE;

        var fileMeta = file.result.meta;

        self.$sdk.notify(`${file.result.title} was uploaded successfully`);

        file.dispatch(EVENT_UPLOAD_COMPLETE, res.data);
        //Upload the next file in the queue
        self.uploadNextFile();
      });

      promise.catch(function (err) {
        var wasCancelled = self.$sdk.api.wasCancelled(err);

        //If it was cancelled
        if (wasCancelled) {
          file.state = STATUS_READY;
        } else {
          file.state = STATUS_ERROR;
          file.dispatch(EVENT_UPLOAD_ERROR, err);
          self.uploadNextFile();
        }
      });
    },
    toggle() {
      if (this.state === STATUS_UPLOADING) {
        this.pause();
      } else {
        this.start();
      }
    },
    async start() {
      var self = this;

      if (self.state === STATUS_UPLOADING) {
        return;
      }

      /////////////////////////////

      //If we haven't chosen any scopes
      if (!self.defaultScopes.length) {
        var actionableFileScopes = self.$sdk.access.actionableScopes(
          self.user,
          "create",
          "file",
        );
        var actionableImageScopes = self.$sdk.access.actionableScopes(
          self.user,
          "create",
          "image",
        );
        var actionableAudioScopes = self.$sdk.access.actionableScopes(
          self.user,
          "create",
          "audio",
        );
        var actionableVideoScopes = self.$sdk.access.actionableScopes(
          self.user,
          "create",
          "video",
        );

        var actionableScopes = self.$sdk.utils.ids([
          ...actionableFileScopes,
          ...actionableImageScopes,
          ...actionableAudioScopes,
          ...actionableVideoScopes,
        ]);

        if (actionableScopes.length === 1) {
          self.defaultScopes = actionableScopes;
        } else {
          var scopes = await self.$sdk.browse("scope", {
            maximum: 0,
          });

          if (!scopes || !scopes.length) {
            return;
          }

          //Get the scope ids
          self.defaultScopes = self.$sdk.utils.ids(scopes);
        }
      }

      /////////////////////////////

      //Set toSTATUS_UPLOADINGand start the next
      //file in the queue
      self.state = STATUS_UPLOADING;
      self.uploadNextFile();
    },
    pause() {
      var self = this;

      if (self.state !== STATUS_UPLOADING) {
        return;
      }

      self.state = STATUS_READY;
      self.queue.forEach(function (entry) {
        self.cancelFile(entry);
      });
    },
    cancel() {
      var self = this;

      if (self.uploading) {
        self.pause();
      } else {
        self.queue.length = 0;
        self.defaultFreeformTags.length = 0;
        self.defaultScopes.length = 0;
      }
    },
  },
};
</script>
